/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { OptionalColumnKey, OptionalColumnVisibility } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';

export type QnaMakerReviewSuggestionCommandBarProps = TableCommandBarProps<QnaMakerQnaStore> & {
    qnasCount: number;
    searchQuery: string;
    onChangeSearchQuery: (query: string) => void;
    isFilterSelected: boolean;
    onFilter: () => void;
    optionalColumnsShown: OptionalColumnVisibility[];
    onChangeOptionalColumnsShown: (key: OptionalColumnKey) => void;
    onAcceptOrRejectSuggestions: (isAccepted: boolean) => void;
    areEditActionsDisabled: boolean;
    isLoading?: boolean;
};

const messages = defineMessages({
    count: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.count',
        defaultMessage: 'pairs'
    },
    searchPlaceholder: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.searchPlaceholder',
        defaultMessage: 'Search pairs'
    },
    acceptSuggestions: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.acceptSuggestions',
        defaultMessage: 'Accept suggestions'
    },
    rejectSuggestions: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.rejectSuggestions',
        defaultMessage: 'Reject suggestions'
    },
    acceptAllSuggestions: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.acceptAllSuggestions',
        defaultMessage: 'Accept all suggestions'
    },
    rejectAllSuggestions: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.rejectAllSuggestions',
        defaultMessage: 'Reject all suggestions'
    },
    show: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.show',
        defaultMessage: 'Show columns'
    },
    filter: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.filter',
        defaultMessage: 'Filter'
    },
    context: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.context',
        defaultMessage: 'Context'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerReviewSuggestionCommandBar.metadata',
        defaultMessage: 'Metadata'
    }
});

/**
 * Command bar rendering all available models and filter options
 */
export const QnaMakerReviewSuggestionCommandBar = observer((props: QnaMakerReviewSuggestionCommandBarProps) => {
    const {
        onFilter,
        qnasCount,
        searchQuery,
        isFilterSelected,
        isLoading = false,
        onChangeSearchQuery,
        optionalColumnsShown,
        areEditActionsDisabled,
        onAcceptOrRejectSuggestions,
        onChangeOptionalColumnsShown
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { isSmartSuggestionEnabled } = rootStore.currentQnaMakerProjectStore;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const showColumnsMenuItems: IContextualMenuItem[] = [
        {
            key: 'context',
            text: localize(messages.context),
            onClick: _event => {
                _event.preventDefault();
                onChangeOptionalColumnsShown('context');
            },
            checked: optionalColumnsShown.find(c => c.key === 'context').isVisible,
            canCheck: true
        },
        {
            key: 'metadata',
            text: localize(messages.metadata),
            onClick: _event => {
                _event.preventDefault();
                onChangeOptionalColumnsShown('metadata');
            },
            checked: optionalColumnsShown.find(c => c.key === 'metadata').isVisible,
            canCheck: true
        }
    ];

    const items: ICommandBarItemProps[] = [
        {
            key: 'acceptSuggestions',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="acceptSuggestionsButtonLanguageToolTip"
                    hidden={canWrite || areEditActionsDisabled || isLoading || !qnasCount || item.disabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            iconProps={{ iconName: 'Add' }}
                            name={localize(messages.acceptSuggestions)}
                            text={localize(isSmartSuggestionEnabled ? messages.acceptSuggestions : messages.acceptAllSuggestions)}
                            onClick={() => onAcceptOrRejectSuggestions(true)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || areEditActionsDisabled || isLoading || !qnasCount || item.disabled}
                        />
                    }
                />
            )
        },
        {
            key: 'rejectSuggestions',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="rejectSuggestionsButtonLanguageToolTip"
                    hidden={canWrite || areEditActionsDisabled || isLoading || !qnasCount || item.disabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            iconProps={{ iconName: 'Delete' }}
                            name={localize(messages.rejectSuggestions)}
                            text={localize(isSmartSuggestionEnabled ? messages.rejectSuggestions : messages.rejectAllSuggestions)}
                            onClick={() => onAcceptOrRejectSuggestions(false)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || areEditActionsDisabled || isLoading || !qnasCount || item.disabled}
                        />
                    }
                />
            )
        },
        {
            key: 'showColumns',
            text: localize(messages.show),
            disabled: isLoading || !qnasCount,
            iconProps: { iconName: 'Column' },
            subMenuProps: { items: showColumnsMenuItems }
        }
    ];

    const farItems = [
        {
            key: 'itemsCount',
            onRender: () =>
                isLoading ? (
                    <Shimmer
                        shimmerElements={[{ type: ShimmerElementType.line, width: '70px', height: 15 }]}
                        styles={{ root: { marginTop: '15px', marginRight: '10px' } }}
                    />
                ) : (
                    <LuisText styles={{ root: { display: 'flex', alignItems: 'center', marginRight: '5px' } }}>
                        {qnasCount} {localize(messages.count)}
                    </LuisText>
                )
        },
        {
            key: 'search',
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    ariaLabel={localize(messages.searchPlaceholder)}
                    size={36}
                    value={searchQuery}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onChangeSearchQuery(newValue)}
                />
            )
        },
        {
            key: 'filter',
            onClick: onFilter,
            text: localize(messages.filter),
            iconProps: { iconName: isFilterSelected ? 'FilterSolid' : 'Filter' }
        }
    ];

    return <LuisCommandBar items={items} farItems={farItems} />;
});
